// src/rms/ads/campaignSource.js
// ============================================================================
// Where servable campaigns come from
// ============================================================================
// Sold campaigns live in the database (rms_ad_campaigns + rms_ad_creatives).
// The static list in adInventory.js remains as house ads and demo inventory, so
// a brand-new install still shows something rather than four empty boxes.
//
// Order of preference when filling a slot:
//   1. sold direct campaigns that are live, funded, on-pace and under frequency cap
//   2. affiliate creatives (paid on conversion, so they bypass the CPM floor)
//   3. house ads selling the ad-free upgrade
//
// Everything below is pure given its inputs, so it can be reasoned about and
// tested without a database.
// ============================================================================

import db from "../data";
import { getCampaigns as staticCampaigns, CPM_FLOOR_CENTS } from "./adInventory";

const DAY_MS = 864e5;

/**
 * Is this campaign eligible to serve right now?
 *
 * Returns null when servable, or a reason string. Reasons are surfaced in the
 * sales console, because "why isn't my campaign delivering?" is the single most
 * common advertiser question and guessing at it wastes everyone's time.
 */
export function ineligibleReason(campaign, { now = new Date(), deliveredToday = 0, operatorSeenToday = 0 } = {}) {
  if (campaign.status !== "live") return `status is ${campaign.status}`;
  if (campaign.is_active === false) return "campaign is inactive";

  if (campaign.starts_at && new Date(campaign.starts_at) > now) return "flight has not started";
  if (campaign.ends_at && new Date(campaign.ends_at) <= now) return "flight has ended";

  if (campaign.budget_cents != null && (campaign.spent_cents || 0) >= campaign.budget_cents) {
    return "budget exhausted";
  }
  if (campaign.goal_impressions != null && (campaign.delivered_impressions || 0) >= campaign.goal_impressions) {
    return "impression goal met";
  }

  // Frequency cap is per operator per day — direct buyers pay for reach, not for
  // showing the same twelve people an ad forty times.
  if (campaign.frequency_cap_per_day != null && operatorSeenToday >= campaign.frequency_cap_per_day) {
    return "frequency cap reached for this operator today";
  }

  // Even pacing: don't spend more than the flight's daily share.
  if (campaign.pacing === "even") {
    const allowance = dailyImpressionAllowance(campaign, now);
    if (allowance != null && deliveredToday >= allowance) return "on pace — daily allowance met";
  }

  return null;
}

/**
 * Impressions this campaign should deliver today under even pacing.
 *
 * Recomputed from what REMAINS rather than from the original goal, so a flight
 * that under-delivered early catches up instead of finishing short.
 */
export function dailyImpressionAllowance(campaign, now = new Date()) {
  if (campaign.goal_impressions == null) return null;

  const end = campaign.ends_at ? new Date(campaign.ends_at) : null;
  if (!end) return null;

  const daysLeft = Math.max(1, Math.ceil((end.getTime() - now.getTime()) / DAY_MS));
  const remaining = Math.max(0, campaign.goal_impressions - (campaign.delivered_impressions || 0));
  return Math.ceil(remaining / daysLeft);
}

/** Flight progress vs time elapsed — the pacing indicator in the console. */
export function pacingStatus(campaign, now = new Date()) {
  if (!campaign.starts_at || !campaign.ends_at || !campaign.goal_impressions) return null;

  const start = new Date(campaign.starts_at).getTime();
  const end = new Date(campaign.ends_at).getTime();
  const total = Math.max(1, end - start);

  const timeElapsedPct = Math.min(100, Math.max(0, ((now.getTime() - start) / total) * 100));
  const deliveredPct = Math.min(100, ((campaign.delivered_impressions || 0) / campaign.goal_impressions) * 100);
  const drift = deliveredPct - timeElapsedPct;

  return {
    timeElapsedPct,
    deliveredPct,
    drift,
    // ±10 points is normal daily noise; beyond that a trafficker should look.
    state: drift < -10 ? "under" : drift > 10 ? "over" : "on_pace",
  };
}

// ----------------------------------------------------------------------------
// Loading
// ----------------------------------------------------------------------------

/** Converts a DB campaign + creative into the shape the ad engine renders. */
function toServable(campaign, creative) {
  const payload = creative?.payload || {};
  return {
    id: campaign.id,
    creativeId: creative?.id || null,
    advertiser: campaign.advertiser_name || campaign.advertiser || campaign.name,
    categories: campaign.ad_categories || [],
    placements: campaign.placements || [],
    cpmCents: campaign.cpm_cents || 0,
    cpcCents: campaign.cpc_cents || 0,
    weight: creative?.weight || campaign.weight || 100,
    isHouseAd: !!campaign.is_house_ad,
    isAffiliate: !!campaign.is_affiliate,
    isDirectSold: true,
    creative: {
      type: creative?.creative_type || "native",
      ...payload,
      clickUrl: creative?.click_url || payload.clickUrl || null,
    },
  };
}

/**
 * Sold campaigns eligible for a vertical + placement, newest-highest-value
 * first. Falls back to the static inventory when nothing is sold, so the
 * product never shows empty boxes.
 */
export async function loadServableCampaigns({ operatorId, adCategory, placement, now = new Date() }) {
  let sold = [];

  try {
    const campaigns = (await db.list("adCampaigns", {})).data || [];
    const creatives = (await db.list("adCreatives", {})).data || [];
    const delivery = (await db.list("adDeliveryDaily", {})).data || [];
    const impressions = (await db.list("adImpressions", { operator_id: operatorId })).data || [];

    const today = now.toISOString().slice(0, 10);

    const deliveredTodayFor = (id) =>
      delivery
        .filter((d) => d.campaign_id === id && d.day === today)
        .reduce((sum, d) => sum + (d.viewable_impressions || 0), 0);

    const seenTodayFor = (id) =>
      impressions.filter((i) => i.campaign_id === id && String(i.occurred_at).slice(0, 10) === today).length;

    sold = campaigns
      .filter((c) => (c.ad_categories || []).includes(adCategory))
      .filter((c) => (c.placements || []).includes(placement))
      .filter(
        (c) =>
          ineligibleReason(c, {
            now,
            deliveredToday: deliveredTodayFor(c.id),
            operatorSeenToday: seenTodayFor(c.id),
          }) === null
      )
      .map((c) => {
        const approved = creatives.filter(
          (cr) => cr.campaign_id === c.id && cr.is_active !== false && cr.review_status === "approved"
        );
        // A campaign with no approved creative cannot serve, however well funded.
        if (!approved.length) return null;
        const sizeMatched = approved.filter((cr) => matchesPlacement(cr, placement));
        const chosen = (sizeMatched.length ? sizeMatched : approved)[0];
        return toServable(c, chosen);
      })
      .filter(Boolean)
      // Highest-value demand first: this is the whole point of selling direct.
      .sort((a, b) => b.cpmCents - a.cpmCents);
  } catch (err) {
    console.warn("Falling back to static ad inventory:", err);
  }

  const fallback = staticCampaigns(adCategory, placement, CPM_FLOOR_CENTS);

  // Sold demand first, then affiliates and house ads from the static pool.
  const backfill = fallback.filter((c) => c.isAffiliate || c.isHouseAd);
  const demo = fallback.filter((c) => !c.isAffiliate && !c.isHouseAd);

  return sold.length ? [...sold, ...backfill] : [...demo, ...backfill];
}

function matchesPlacement(creative, placement) {
  if (placement === "banner_top") return creative.iab_size === "leaderboard" || creative.width_px >= 468;
  return creative.width_px <= 336;
}

const campaignSource = {
  loadServableCampaigns,
  ineligibleReason,
  dailyImpressionAllowance,
  pacingStatus,
};

export default campaignSource;
